import { useState } from 'react'
import { Button, Textarea, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post } from '../lib/api'
import { ago } from '../lib/format'
import { LoadingBlock, ErrorBlock } from './Loading'

type Message = { id: string; from_owner: boolean; body: string; created_at: string }
type Thread = { business: string; default_message: string; messages: Message[] }

/** A responder and a business's owner: a button until it is opened, so a lead page stays short. */
export function OwnerMessages({ id }: { id: string }) {
  const [open, setOpen] = useState(false)
  if (!open) return <div><Button variant="ghost" onClick={() => setOpen(true)}>Message the owner</Button></div>
  return <OwnerThread id={id} />
}

function OwnerThread({ id }: { id: string }) {
  const { data, error, reload } = useApi<Thread>(`/api/my/clients/${id}/messages`)
  const toast = useToast()
  const [text, setText] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  // A first message starts from the default words, ready to edit.
  const value = text ?? (data.messages.length ? '' : data.default_message)
  const send = async () => {
    setBusy(true)
    try { await post(`/api/my/clients/${id}/messages`, { body: value }); setText(''); reload(); toast('Sent', 'pos') }
    catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <div className="owner-thread">
      <MessageList messages={data.messages} mine={(m) => !m.from_owner} theirName={data.business} />
      <Textarea rows={2} aria-label={`Message ${data.business}`} value={value} onChange={(e) => setText(e.target.value)} />
      <div><Button variant="secondary" onClick={send} disabled={busy || !value.trim()}>{busy ? 'Sending' : 'Send'}</Button></div>
    </div>
  )
}

/** The messages of one thread, oldest first. Shared with the portal. */
export function MessageList({ messages, mine, theirName }: { messages: Message[]; mine: (m: Message) => boolean; theirName: string }) {
  return (
    <>
      {messages.map((m) => (
        <div key={m.id} className={mine(m) ? 'owner-msg is-mine' : 'owner-msg'}>
          <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>{mine(m) ? 'You' : theirName}, {ago(m.created_at)}</span>
          <span style={{ whiteSpace: 'pre-wrap' }}>{m.body}</span>
        </div>
      ))}
    </>
  )
}
